import { DateTime } from 'luxon';
import EventDTO from '../models/event';

/**
 * Client-side search for the home (AT-1804). Runs over the events the home
 * already loads (`GET /events/all/available`), so it needs no backend
 * endpoint. Matches name, venue/place, city, province, producer and
 * categories; ignores accents and case. When the exact search finds nothing
 * it retries tolerating typos (1 per word of 5–6 letters, 2 from 7).
 */

export const AR_TIMEZONE = 'America/Argentina/Buenos_Aires';

export type DateChip = 'hoy' | 'finde' | 'semana';

export const DATE_CHIPS: { id: DateChip; label: string }[] = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'finde', label: 'Este finde' },
  { id: 'semana', label: 'Esta semana' },
];

export interface EventSearchFilter {
  query: string;
  chip?: DateChip;
  province?: string;
}

export const normalizeText = (s?: string | null): string => (s ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

export const tokenize = (query: string): string[] => normalizeText(query)
  .split(/[\s,.;:/-]+/)
  .filter(Boolean);

export const eventVenueName = (e: EventDTO): string => e.venue?.name || e.place || '';

/** "Quattro, San Juan" — skips the city when the venue text already has it. */
export const eventPlaceLabel = (e: EventDTO): string => {
  const venue = eventVenueName(e);
  const city = e.city || '';
  if (!city) { return venue; }
  if (!venue) { return city; }
  return normalizeText(venue).includes(normalizeText(city)) ? venue : `${venue}, ${city}`;
};

const haystackOf = (e: EventDTO): string => normalizeText([
  e.name,
  e.place,
  e.venue?.name,
  e.city,
  e.province,
  e.business_name,
  ...(e.categories ?? []).map((c) => c.name),
].filter(Boolean).join(' '));

/**
 * Smallest edit distance between `token` and any prefix of `word` (so a
 * half-typed word still counts), giving up as soon as it exceeds `max`.
 */
const prefixDistance = (token: string, word: string, max: number): number => {
  let prev = Array.from({ length: word.length + 1 }, (_, j) => j);
  for (let i = 1; i <= token.length; i += 1) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= word.length; j += 1) {
      const cost = token[i - 1] === word[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      rowMin = Math.min(rowMin, cur[j]);
    }
    if (rowMin > max) { return max + 1; }
    prev = cur;
  }
  return Math.min(...prev);
};

// Typos allowed per word: none under 5 letters ("pier" must not find
// "viernes"), 1 for 5–6 letters, 2 from 7 letters ("halowen" → "halloween").
const allowedTypos = (token: string): number => {
  if (token.length >= 7) { return 2; }
  if (token.length >= 5) { return 1; }
  return 0;
};

const tokenMatches = (token: string, haystack: string, words: string[], fuzzy: boolean): boolean => {
  if (haystack.includes(token)) { return true; }
  const max = fuzzy ? allowedTypos(token) : 0;
  if (max === 0) { return false; }
  return words.some((w) => prefixDistance(token, w, max) <= max);
};

const matchesTokens = (text: string, tokens: string[], fuzzy: boolean): boolean => {
  const words = text.split(/[^a-z0-9]+/).filter(Boolean);
  return tokens.every((t) => tokenMatches(t, text, words, fuzzy));
};

/** [from, to] window in Argentina time for a date chip. */
export const dateChipWindow = (chip: DateChip, now: DateTime = DateTime.now().setZone(AR_TIMEZONE)) => {
  const start = now.startOf('day');
  // Luxon weekday: Monday = 1 … Sunday = 7.
  const endOfWeek = start.plus({ days: 7 - start.weekday }).endOf('day');
  if (chip === 'hoy') { return [start, start.endOf('day')] as const; }
  if (chip === 'semana') { return [start, endOfWeek] as const; }
  const friday = start.weekday >= 5 ? start : start.plus({ days: 5 - start.weekday });
  return [friday, endOfWeek] as const;
};

const nowInAR = () => DateTime.now().setZone(AR_TIMEZONE);

const inChip = (e: EventDTO, chip: DateChip | undefined, now: DateTime): boolean => {
  if (!chip) { return true; }
  const [from, to] = dateChipWindow(chip, now);
  const d = DateTime.fromISO(e.date, { zone: AR_TIMEZONE });
  return d >= from && d <= to;
};

/** Events matching the filter, best matches first (stable within a score). */
export const searchEvents = (
  events: EventDTO[],
  filter: EventSearchFilter,
  now: DateTime = nowInAR(),
): EventDTO[] => {
  const tokens = tokenize(filter.query);
  const q = normalizeText(filter.query).trim();
  const base = events
    .filter((e) => (!filter.province || e.province === filter.province) && inChip(e, filter.chip, now));
  if (!tokens.length) { return base; }
  const run = (fuzzy: boolean) => base
    .map((e, index) => {
      if (!matchesTokens(haystackOf(e), tokens, fuzzy)) { return null; }
      const name = normalizeText(e.name);
      let score = 1;
      if (tokens.every((t) => name.includes(t))) { score = 2; }
      if (name.startsWith(q)) { score = 3; }
      return { e, index, score };
    })
    .filter((x): x is { e: EventDTO; index: number; score: number } => !!x);
  // Typo tolerance only kicks in when the exact search finds nothing, so a
  // real match never comes with look-alike noise.
  let scored = run(false);
  if (!scored.length) { scored = run(true); }
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.map((x) => x.e);
};

export interface SearchGroup {
  name: string;
  count: number;
  sub: string;
}

const plural = (n: number) => (n === 1 ? '1 evento' : `${n} eventos`);

/** Venues or producers whose own name matches the query (for the side column). */
export const searchGroups = (
  events: EventDTO[],
  filter: EventSearchFilter,
  keyOf: (e: EventDTO) => string,
  withCity = false,
  now: DateTime = nowInAR(),
): SearchGroup[] => {
  const tokens = tokenize(filter.query);
  if (!tokens.length) { return []; }
  const base = events
    .filter((e) => (!filter.province || e.province === filter.province) && inChip(e, filter.chip, now));
  const collect = (fuzzy: boolean) => {
    const map = new Map<string, { name: string; count: number; city: string }>();
    base.forEach((e) => {
      const name = keyOf(e)?.trim();
      if (!name) { return; }
      const text = normalizeText(withCity ? `${name} ${e.city ?? ''}` : name);
      if (!matchesTokens(text, tokens, fuzzy)) { return; }
      const key = normalizeText(name);
      const cur = map.get(key) ?? { name, count: 0, city: e.city ?? '' };
      cur.count += 1;
      map.set(key, cur);
    });
    return map;
  };
  let map = collect(false);
  if (!map.size) { map = collect(true); }
  return Array.from(map.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)
    .map((g) => ({ name: g.name, count: g.count, sub: withCity && g.city ? `${plural(g.count)} · ${g.city}` : plural(g.count) }));
};

/** Splits text into highlighted / plain parts for the matched tokens. */
export const highlightParts = (text: string, query: string): { text: string; hit: boolean }[] => {
  const tokens = tokenize(query);
  if (!tokens.length) { return [{ text, hit: false }]; }
  const norm = normalizeText(text);
  // NFD + strip keeps one char per precomposed char, so indexes line up.
  if (norm.length !== text.length) { return [{ text, hit: false }]; }
  const mask = new Array(text.length).fill(false);
  tokens.forEach((t) => {
    let i = norm.indexOf(t);
    while (i > -1) {
      for (let k = i; k < i + t.length; k += 1) mask[k] = true;
      i = norm.indexOf(t, i + t.length);
    }
  });
  const parts: { text: string; hit: boolean }[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const last = parts[parts.length - 1];
    if (last && last.hit === mask[i]) {
      last.text += text[i];
    } else {
      parts.push({ text: text[i], hit: mask[i] });
    }
  }
  return parts;
};

export const describeFilter = (filter: EventSearchFilter): string => {
  const chip = DATE_CHIPS.find((c) => c.id === filter.chip)?.label;
  const bits = [filter.query.trim() ? `“${filter.query.trim()}”` : '', chip ?? '', filter.province ?? ''].filter(Boolean);
  return bits.join(' · ');
};

export const SUPPORT_WHATSAPP_URL = 'https://wa.me/542645515045?text=%C2%A1Hola!%20No%20encuentro%20un%20evento%20en%20Avantti';
